import { supabase } from '../../shared/supabase/supabaseClient';

export type ServiceReminder = {
  id: string;
  vehicleId: string;
  label: string;
  triggerKm: number | null;
  triggerDays: number | null;
  lastCompletedKm: number | null;
  lastCompletedAt: string | null;
  snoozedUntil: string | null;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ServiceReminderDraft = {
  id?: string;
  vehicleId: string;
  label: string;
  triggerKm: number | null;
  triggerDays: number | null;
  notes: string | null;
};

export type DueServiceReminder = ServiceReminder & {
  dueReason: 'km' | 'tiempo' | null;
};

type ReminderRow = {
  id: string;
  vehicle_id: string;
  label: string;
  trigger_km: number | null;
  trigger_days: number | null;
  last_completed_km: number | null;
  last_completed_at: string | null;
  snoozed_until: string | null;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

function mapRow(r: ReminderRow): ServiceReminder {
  return {
    id: r.id,
    vehicleId: r.vehicle_id,
    label: r.label,
    triggerKm: r.trigger_km,
    triggerDays: r.trigger_days,
    lastCompletedKm: r.last_completed_km,
    lastCompletedAt: r.last_completed_at,
    snoozedUntil: r.snoozed_until,
    isActive: r.is_active,
    notes: r.notes,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listVehicleServiceReminders(
  vehicleId: string,
  includeInactive = false,
): Promise<ServiceReminder[]> {
  const { data, error } = await supabase.rpc('list_vehicle_service_reminders', {
    p_vehicle_id: vehicleId,
    p_include_inactive: includeInactive,
  });
  if (error) throw new Error(error.message);
  return ((data ?? []) as ReminderRow[]).map(mapRow);
}

export async function listDueServiceReminders(): Promise<DueServiceReminder[]> {
  const { data, error } = await supabase.rpc('list_due_service_reminders', {
    // Org-wide por diseño (#1292): p_branch_id es reservado/no-op.
    p_branch_id: null,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as (ReminderRow & { due_reason: string | null })[];
  return rows.map((r) => ({ ...mapRow(r), dueReason: r.due_reason as DueServiceReminder['dueReason'] }));
}

export async function upsertServiceReminder(
  organizationId: string,
  draft: ServiceReminderDraft,
): Promise<string> {
  const { data, error } = await supabase.rpc('upsert_service_reminder', {
    p_id: draft.id ?? null,
    p_organization_id: organizationId,
    p_vehicle_id: draft.vehicleId,
    p_label: draft.label,
    p_trigger_km: draft.triggerKm ?? null,
    p_trigger_days: draft.triggerDays ?? null,
    p_notes: draft.notes ?? null,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function completeServiceReminder(
  reminderId: string,
  completedKm?: number | null,
): Promise<void> {
  const { error } = await supabase.rpc('complete_service_reminder', {
    p_reminder_id: reminderId,
    p_completed_km: completedKm ?? null,
  });
  if (error) throw new Error(error.message);
}

export async function snoozeServiceReminder(
  reminderId: string,
  days = 30,
): Promise<void> {
  const { error } = await supabase.rpc('snooze_service_reminder', {
    p_reminder_id: reminderId,
    p_days: days,
  });
  if (error) throw new Error(error.message);
}

export async function deleteServiceReminder(reminderId: string): Promise<void> {
  const { error } = await supabase.rpc('delete_service_reminder', {
    p_reminder_id: reminderId,
  });
  if (error) throw new Error(error.message);
}
